import React, { useState, useEffect } from 'react';
import { X, Edit2, CheckCircle } from 'lucide-react';
import confetti from 'canvas-confetti';
import { closeDeal, inviteCollaborator, removeCollaborator } from '../../services/firestoreService';
import { logDealClosed } from '../../services/activityLogService';
import { sendDealClosedSMS } from '../../services/notificationService';
import Sidebar from '../layout/Sidebar';
import AccordionBoard from '../tasks/AccordionBoard';
import TaskDetailPanel from '../tasks/TaskDetailPanel';
import DealFinancials from '../tasks/DealFinancials';
import NewProjectModal from '../tasks/NewProjectModal';
import TeamMemberModal from '../tasks/TeamMemberModal';
import EditProfileModal from '../tasks/EditProfileModal';
import EditProjectModal from '../tasks/EditProjectModal';
import ArchiveConfirmModal from '../tasks/ArchiveConfirmModal';
import ToolsTab from '../tools/ToolsTab';
import DeleteConfirmModal from '../tasks/DeleteConfirmModal';

const ProjectView = ({ onTasksReloaded,
  activeProjects, selectedProject, selectedTask, teamMembers, currentView,
  onSelectProject, onPipeline, onDashboard, onArchive, onNewProject,
  onTeamSettings, onMyProfile, onLogout, getDealTitle, isAdmin,
  getTaskColor, isTaskUrgent, onTaskClick, updateProject,
  onCloseTask, onUpdateTask,
  showNewProjectModal, onCloseNewProject, createProject,
  showTeamModal, onCloseTeamModal, addTeamMember, removeTeamMember, onEditTeamMember,
  showEditProfileModal, editProfileMember, editProfileMode,
  onCloseEditProfile, onSaveEditProfile,
  showArchiveModal, onOpenArchiveModal, onArchiveProject, onCloseArchiveModal,
  showDeleteModal, dealToDelete, onDeleteDeal, onConfirmDelete, onCancelDelete,
  showEditProjectModal, onOpenEditProject, onCloseEditProject, onSaveEditProject,
  isSuperAdmin, onSuperAdmin, onHelpSupport, user, orgId, onUpdateTaskById, updateTasksBatch,
}) => {
  const isMobile = window.innerWidth < 768;
  const [showClosdModal, setShowClosdModal] = useState(false);
  const [showArchivePrompt, setShowArchivePrompt] = useState(false);
  const [showCollabModal, setShowCollabModal] = useState(false);
const [activeTab, setActiveTab] = useState('tasks');

  useEffect(() => {
    if (selectedProject?.ucDate && !selectedProject?.variableDaysSet) {
      setActiveTab('financials');
    }
  }, [selectedProject?.id, selectedProject?.ucDate, selectedProject?.variableDaysSet]);
  const [collabEmail, setCollabEmail] = useState('');
  const [collabStatus, setCollabStatus] = useState(null);
  const [collabLoading, setCollabLoading] = useState(false);

  const handleInviteCollaborator = async () => {
    if (!collabEmail.trim()) return;
    setCollabLoading(true);
    setCollabStatus(null);
    const result = await inviteCollaborator(orgId, selectedProject.id, collabEmail.trim().toLowerCase());
    setCollabStatus(result);
    setCollabLoading(false);
    if (result.success) {
      setCollabEmail('');
      updateProject(selectedProject.id, {
        collaborators: [
          ...(selectedProject.collaborators || []),
          { userId: result.inviteeId, email: collabEmail.trim().toLowerCase() },
        ],
      });
    }
  };

  const handleRemoveCollaborator = async (userId) => {
    await removeCollaborator(orgId, selectedProject.id, userId);
    updateProject(selectedProject.id, {
      collaborators: (selectedProject.collaborators || []).filter(c => c.userId !== userId),
    });
  };

  const fireConfetti = () => {
    confetti({ particleCount: 150, spread: 70, origin: { y: 0.6 } });
    setTimeout(() => {
      confetti({ particleCount: 100, spread: 100, origin: { y: 0.5, x: 0.3 } });
    }, 200);
    setTimeout(() => {
      confetti({ particleCount: 100, spread: 100, origin: { y: 0.5, x: 0.7 } });
    }, 400);
  };

  const handleConfirmClosd = async () => {
    setShowClosdModal(false);
    try {
      await closeDeal(orgId, selectedProject.id);
      await updateProject(selectedProject.id, { status: 'closed', closedAt: new Date().toISOString() });
      logDealClosed(orgId, user.uid, { projectId: selectedProject.id, clientName: selectedProject.clientName || '', propertyAddress: selectedProject.propertyAddress || '' });
      const closedByName = user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email : 'A team member';
      fireConfetti();
      sendDealClosedSMS(selectedProject, closedByName);
      setTimeout(() => {
        setShowArchivePrompt(true);
      }, 2000);
    } catch (err) {
      console.error('Error closing deal:', err);
    }
  };

  const handleToggleStatus = async (task) => {
    const newStatus = task.forceStatus || (task.status === 'complete' ? 'not-started' : 'complete');
    onUpdateTaskById(selectedProject.id, task.id, { status: newStatus });
  };

  

  const handleArchiveAfterClosd = () => {
    setShowArchivePrompt(false);
    onArchiveProject();
  };

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar
        projects={activeProjects}
        onSelectProject={onSelectProject}
        onPipeline={onPipeline}
        onDashboard={onDashboard}
        onArchive={onArchive}
        onNewProject={onNewProject}
        onTeamSettings={onTeamSettings}
        onMyProfile={onMyProfile}
        onLogout={onLogout}
        currentView={currentView}
        getDealTitle={getDealTitle}
        isAdmin={isAdmin}
        isSuperAdmin={isSuperAdmin}
        onSuperAdmin={onSuperAdmin}
        onHelpSupport={onHelpSupport}
        user={user}
        orgId={orgId}
        selectedProjectId={selectedProject?.id}
      />
      <div className="flex-1 overflow-auto">
        <div className="bg-white border-b p-4" style={{ position: 'sticky', top: 0, zIndex: 10, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
          <div className="flex flex-col gap-2 mb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <h2 style={{ fontSize: isMobile ? 18 : 24, fontWeight: 700 }}>{getDealTitle(selectedProject)}</h2>
                <button onClick={onOpenEditProject}
                  className="p-1 hover:bg-gray-100 rounded" title="Edit deal details">
                  <Edit2 className="w-4 h-4" style={{ color: '#516469' }} />
                </button>
              </div>
              <div className="flex items-center gap-2">
                {selectedProject?.status === 'archived' ? (
                  <button
                    onClick={() => onDeleteDeal(selectedProject)}
                    className="rounded font-semibold flex items-center gap-1"
                    style={{ backgroundColor: '#FEE2E2', color: '#DC2626', border: '2px solid #FCA5A5', padding: isMobile ? '5px 8px' : '8px 16px', fontSize: isMobile ? 11 : 14 }}>
                    <X className="w-3 h-3" /> Delete
                  </button>
                ) : (
                  <>
                    {selectedProject?.status === 'closed' ? (
                      <button
                        onClick={() => updateProject(selectedProject.id, { status: 'active', closedAt: null })}
                        className="rounded font-semibold flex items-center gap-1"
                        style={{ backgroundColor: '#EFF6FF', color: '#2563EB', border: '2px solid #93C5FD', padding: isMobile ? '5px 8px' : '8px 16px', fontSize: isMobile ? 11 : 14 }}>
                        <CheckCircle className="w-3 h-3" /> Reopen ✓
                      </button>
                    ) : null}
                    {isAdmin && (
                      <button
                        onClick={() => { setShowCollabModal(true); setCollabStatus(null); setCollabEmail(''); }}
                        className="rounded font-semibold flex items-center gap-1"
                        style={{ backgroundColor: '#EFF6FF', color: '#2563EB', border: '1px solid #93C5FD', padding: isMobile ? '5px 8px' : '8px 16px', fontSize: isMobile ? 11 : 14 }}>
                        🤝 {isMobile ? 'Collab' : 'Invite Collaborator'}
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500">Client:</span>{' '}
              <span className="font-medium">{selectedProject?.clientName}</span>
            </div>
            <div>
              <span className="text-gray-500">Type:</span>{' '}
              <span className="font-medium">{selectedProject?.type}</span>
            </div>
            <div>
              <span className="text-gray-500">Phone:</span>{' '}
              <span className="font-medium">
                {selectedProject?.phone
                  ? selectedProject.phone.split(',').map(n => {
                      const d = n.trim().replace(/\D/g, '');
                      return d.length === 10 ? `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6)}` : n.trim();
                    }).filter(Boolean).join(' · ')
                  : 'Not provided'}
              </span>
            </div>
            <div>
              <span className="text-gray-500">Email:</span>{' '}
              <span className="font-medium">{selectedProject?.email || 'Not provided'}</span>
            </div>
            {selectedProject?.contractDate && (
              <p className="text-sm">
                <span className="text-gray-500">Under Contract Date:</span>{' '}
                <span className="font-medium">{selectedProject?.contractDate}</span>
              </p>
            )}
            {selectedProject?.opposingAgentName && (
              <div className="col-span-2">
                <span className="text-gray-500 text-sm">
                  {selectedProject?.type === 'Seller' ? "Buyer's Agent:" : "Seller's Agent:"}
                </span>{' '}
                <span className="font-medium text-sm">{selectedProject.opposingAgentName}</span>
                {selectedProject?.opposingAgentPhone && (
                  <span className="text-sm" style={{ color: '#516469' }}> · {selectedProject.opposingAgentPhone}</span>
                )}
                {selectedProject?.opposingAgentEmail && (
                  <span className="text-sm" style={{ color: '#516469' }}> · {selectedProject.opposingAgentEmail}</span>
                )}
              </div>
            )}
            
          </div>
        </div>
        <div className="p-4">
          {/* Tab Bar */}
          <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid #071D39', marginBottom: 0, backgroundColor: 'transparent', padding: '0 0 0 4px' }}>
            {['📋 Tasks', '🛠️ Tools', ...(isAdmin ? ['💰 Financials'] : [])].map((label) => {
              const key = label.includes('Tasks') ? 'tasks' : label.includes('Tools') ? 'tools' : 'financials';
              return (
                <button
                  key={key}
                  onClick={() => setActiveTab(key)}
                  style={{
                    padding: '10px 28px',
                    background: activeTab === key ? '#071D39' : '#e5e7eb',
                    border: '1px solid #071D39',
                    borderBottom: activeTab === key ? '1px solid #071D39' : '1px solid #071D39',
                    borderRadius: '10px 10px 0 0',
                    marginBottom: -1,
                    fontWeight: 700,
                    color: activeTab === key ? '#ffffff' : '#071D39',
                    fontSize: 15,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    letterSpacing: '-0.2px',
                    boxShadow: activeTab === key ? '0 -2px 8px rgba(7,29,57,0.15)' : 'none',
                  }}>
                  {label}
                </button>
              );
            })}
          </div>

          {/* Tab Content */}
          {activeTab === 'tasks' && (
            <>
              <AccordionBoard
                project={{ ...selectedProject, orgId: selectedProject?.orgId || orgId }}
                onTaskClick={onTaskClick}
                onToggleStatus={handleToggleStatus}
                onToggleAll={(allDone, tasks) => {
                const newStatus = allDone ? 'not-started' : 'complete';
                const taskUpdates = tasks
                  .filter(t => t.status !== newStatus)
                  .map(t => ({ taskId: t.id, updates: { status: newStatus } }));
                updateTasksBatch(selectedProject.id, taskUpdates);
              }}
                getTaskColor={getTaskColor}
                isTaskUrgent={isTaskUrgent}
              />
              {selectedProject?.status !== 'closed' && selectedProject?.status !== 'archived' && (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: '8px 0 24px' }}>
                  <button
                    onClick={() => setShowClosdModal(true)}
                    className="rounded font-semibold flex items-center gap-1"
                    style={{ backgroundColor: '#75BB2E', color: '#FFFFFF', border: 'none', padding: '12px 32px', fontSize: 16, borderRadius: 8 }}>
                    <CheckCircle className="w-4 h-4" /> This Deal is CLOSd! 🎉
                  </button>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <button
                      onClick={onOpenArchiveModal}
                      style={{ backgroundColor: 'transparent', color: '#89A8B1', border: '1px solid #d0d4da', borderRadius: 6, padding: '6px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                      Archive Deal
                    </button>
                    {isAdmin && (
                      <button
                        onClick={() => onDeleteDeal(selectedProject)}
                        style={{ backgroundColor: 'transparent', color: '#DC2626', border: '1px solid #FCA5A5', borderRadius: 6, padding: '6px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>
                        Delete Deal
                      </button>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
          {activeTab === 'tools' && <ToolsTab projectId={selectedProject?.id} />}
          {activeTab === 'financials' && isAdmin && (
            <div style={{ backgroundColor: '#f4f5f7', minHeight: '100%', padding: '24px' }}>
              <div style={{ backgroundColor: '#ffffff', borderRadius: 12, border: '1px solid #d0d4da', padding: '24px', maxWidth: 600 }}>
                <DealFinancials
                  selectedProject={selectedProject}
                  updateProject={updateProject}
                  orgId={orgId}
                  teamMembers={teamMembers}
                  onTasksReloaded={onTasksReloaded}
                />
              </div>
            </div>
          )}
        </div>
      </div>
      {selectedTask && (
        <TaskDetailPanel
          task={selectedTask} project={selectedProject}
          teamMembers={teamMembers} onClose={onCloseTask} onUpdate={onUpdateTask}
        />
      )}
      {showNewProjectModal && (
        <NewProjectModal onClose={onCloseNewProject} onSave={createProject} teamMembers={teamMembers} />
      )}
      {showTeamModal && (
        <TeamMemberModal
          onClose={onCloseTeamModal} onAdd={addTeamMember}
          onRemove={removeTeamMember} onEdit={onEditTeamMember}
          teamMembers={teamMembers} isAdmin={isAdmin}
        />
      )}
      {showEditProfileModal && editProfileMember && (
        <EditProfileModal
          member={editProfileMember} mode={editProfileMode}
          isAdmin={isAdmin} onClose={onCloseEditProfile} onSave={onSaveEditProfile}
        />
      )}
      {showArchiveModal && (
        <ArchiveConfirmModal
          projectAddress={selectedProject?.propertyAddress}
          onConfirm={onArchiveProject} onCancel={onCloseArchiveModal}
        />
      )}
      {showDeleteModal && dealToDelete && (
        <DeleteConfirmModal
          projectAddress={dealToDelete.propertyAddress}
          onConfirm={onConfirmDelete}
          onCancel={onCancelDelete}
          onArchive={dealToDelete.status !== 'archived' ? () => { onCancelDelete(); onOpenArchiveModal(); } : null}
        />
      )}
      {showEditProjectModal && (
        <EditProjectModal
          project={selectedProject}
          teamMembers={teamMembers}
          onClose={onCloseEditProject} onSave={onSaveEditProject}
        />
      )}

      {/* ===== CLOSd CONFIRMATION MODAL ===== */}
      {showClosdModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4 shadow-xl">
            <h3 className="text-xl font-bold mb-2" style={{ color: '#071D39', fontFamily: 'AvenirNext' }}>
              🎉 Are you sure this deal is CLOSd?
            </h3>
            <p className="text-sm mb-6" style={{ color: '#516469' }}>
              This will mark <strong>{selectedProject?.propertyAddress || selectedProject?.clientName}</strong> as CLOSd. You can still archive it afterward.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowClosdModal(false)}
                className="px-4 py-2 rounded font-semibold"
                style={{ backgroundColor: '#F3F4F6', color: '#516469' }}>
                Cancel
              </button>
              <button
                onClick={handleConfirmClosd}
                className="px-4 py-2 rounded font-semibold"
                style={{ backgroundColor: '#75BB2E', color: '#FFFFFF' }}>
                Yes, This Deal is CLOSd!
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== INVITE COLLABORATOR MODAL ===== */}
      {showCollabModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4 shadow-xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold" style={{ color: '#071D39' }}>🤝 Invite Collaborator</h3>
              <button onClick={() => setShowCollabModal(false)}><X className="w-5 h-5" style={{ color: '#516469' }} /></button>
            </div>
            <p className="text-sm mb-4" style={{ color: '#516469' }}>
              Enter the email address of a CLOSd user to give them access to this deal. They'll see it in their org switcher as a User.
            </p>
            <input
              type="email"
              placeholder="their@email.com"
              value={collabEmail}
              onChange={e => setCollabEmail(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleInviteCollaborator()}
              className="w-full border rounded px-3 py-2 text-sm mb-3"
              style={{ borderColor: '#89A8B1', outline: 'none' }}
            />
            {collabStatus && (
              <p className="text-sm mb-3" style={{ color: collabStatus.success ? '#75BB2E' : '#DC2626' }}>
                {collabStatus.success
                  ? `✅ ${collabStatus.inviteeName || 'Collaborator'} has been added to this deal.`
                  : `❌ ${collabStatus.reason}`}
              </p>
            )}
            <button
              onClick={handleInviteCollaborator}
              disabled={collabLoading || !collabEmail.trim()}
              className="w-full py-2 rounded font-semibold mb-4"
              style={{ backgroundColor: '#75BB2E', color: '#FFFFFF', opacity: collabLoading || !collabEmail.trim() ? 0.6 : 1 }}>
              {collabLoading ? 'Looking up...' : 'Send Invite'}
            </button>
            {(selectedProject?.collaborators || []).length > 0 && (
              <>
                <div className="text-xs font-semibold mb-2" style={{ color: '#516469' }}>CURRENT COLLABORATORS</div>
                {(selectedProject.collaborators || []).map(c => (
                  <div key={c.userId} className="flex items-center justify-between py-2 border-b" style={{ borderColor: '#F0F0F0' }}>
                    <span className="text-sm" style={{ color: '#071D39' }}>{c.email}</span>
                    <button
                      onClick={() => handleRemoveCollaborator(c.userId)}
                      className="text-xs px-2 py-1 rounded"
                      style={{ backgroundColor: '#FEE2E2', color: '#DC2626' }}>
                      Remove
                    </button>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      )}

      {/* ===== ARCHIVE AFTER CLOSd PROMPT ===== */}
      {showArchivePrompt && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4 shadow-xl text-center">
            <h3 className="text-xl font-bold mb-2" style={{ color: '#071D39', fontFamily: 'AvenirNext' }}>
              🥳 Congratulations!
            </h3>
            <p className="text-sm mb-6" style={{ color: '#516469' }}>
              This deal is officially CLOSd! Would you like to move it to the Archive now?
            </p>
            <div className="flex justify-center gap-3">
              <button
                onClick={() => setShowArchivePrompt(false)}
                className="px-4 py-2 rounded font-semibold"
                style={{ backgroundColor: '#F3F4F6', color: '#516469' }}>
                Keep it here for now
              </button>
              <button
                onClick={handleArchiveAfterClosd}
                className="px-4 py-2 rounded font-semibold"
                style={{ backgroundColor: '#75BB2E', color: '#FFFFFF' }}>
                Move to Archive
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectView;